// components/PurchaseOrderPDF.jsx
import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import moment from "moment";

const styles = StyleSheet.create({
  page: {
    fontFamily: "Helvetica",
    fontSize: 9.5,
    padding: 15,
    lineHeight: 1.2,
    color: "#000",
  },
  bold: {
    fontFamily: "Helvetica-Bold",
  },
  row: {
    flexDirection: "row",
  },
  column: {
    flex: 1,
  },
});

const twoDecimal = (x) => {
  const num = Number.parseFloat(x).toFixed(2);
  const [integerPart, decimalPart] = num.split(".");
  const formattedInteger = integerPart.replace(/(\d)(?=(\d\d)+\d$)/g, "$1,");
  return `${formattedInteger}.${decimalPart}`;
};

function roundUpToInteger(value) {
  const roundedValue = Number.parseFloat(value);
  const num = roundedValue.toFixed(2);
  const [integerPart, decimalPart] = num.split(".");
  const formattedInteger = integerPart.replace(/(\d)(?=(\d\d)+\d$)/g, "$1,");
  return `${formattedInteger}.${decimalPart}`;
}

const PDFDocument = ({
  poDetails,
  paymentList,
  poid,
  projectDetails,
  ClassificationData,
  companyLogo,
}) => {
  function stripHtml(html) {
    if (!html) return "";
    return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  }
  const poNo = poDetails?.po_no || "";
  const match = poNo.match(/T(\d+)\//);
  const extractedNumber = match ? match[1] : "";

  return (
    <Document>
      <Page size="A4" style={styles.page}>

        {/* ── HEADER ROW: Logo LEFT | Blue Title Box RIGHT ── */}
        <View
          style={{
            flexDirection: "row",
            alignItems: "stretch",
            border: "1 solid #ccc",
            marginBottom: 0,
          }}
        >
          {/* Left: Logo block */}
          <View
            style={{
              width: "35%",
              alignItems: "center",
              justifyContent: "center",
              padding: 6,
              borderRight: "1 solid #ccc",
            }}
          >
            <Image
              src={companyLogo}
              style={{ width: 130, height: 55, objectFit: "contain" }}
            />
          </View>

          {/* Right: "Purchase Order" blue title */}
          <View
            style={{
              width: "65%",
              backgroundColor: "#0056b3",
              alignItems: "center",
              justifyContent: "center",
              paddingVertical: 10,
            }}
          >
            <Text
              style={{
                color: "white",
                fontSize: 16,
                fontFamily: "Helvetica-Bold",
                textAlign: "center",
              }}
            >
              {poDetails.order_type == "1" ? "Purchase Order" : "Work Order"}
            </Text>
          </View>
        </View>

        {/* ── SUBTITLE: PO reference line ── */}
        <View
          style={{
            borderLeft: "1 solid #ccc",
            borderRight: "1 solid #ccc",
            borderBottom: "1 solid #ccc",
            paddingVertical: 4,
            paddingHorizontal: 8,
            marginBottom: 0,
          }}
        >
          <Text
            style={{
              fontSize: 9,
              textAlign: "center",
              fontFamily: "Helvetica-Bold",
            }}
          >
            {extractedNumber} {poDetails.vendor_name}{" "}
            {moment(poDetails.po_date).format("DD-MM-YYYY")} @
            {poDetails.project_name}
          </Text>
        </View>

        {/* ── COMPANY INFO LEFT | PO DETAILS RIGHT ── */}
        <View
          style={{
            flexDirection: "row",
            borderLeft: "1 solid #ccc",
            borderRight: "1 solid #ccc",
            borderBottom: "1 solid #ccc",
            marginBottom: 0,
          }}
        >
          {/* Left: Company info */}
          <View
            style={{
              width: "50%",
              padding: 7,
            }}
          >
            <Text
              style={{
                fontFamily: "Helvetica-Bold",
                fontSize: 10.5,
                color: "#0056b3",
                marginBottom: 2,
              }}
            >
              {poDetails.company_Name}
            </Text>
            <Text style={{ fontSize: 9, marginBottom: 1 }}>
              {poDetails
                ? poDetails.company_address?.trim().replace(/\s+/g, " ")
                : `KG Nest Plot No.108, Perumal Koil St Alapakkam`}
            </Text>
            <Text style={{ fontSize: 9, marginBottom: 1 }}>
              {poDetails
                ? `${poDetails.company_city} - ${poDetails.company_pincode}`
                : `Chennai - 600116`}
            </Text>
            <Text style={{ fontSize: 9, marginBottom: 1 }}>
              TamilNadu, India
            </Text>
            <Text
              style={{
                fontSize: 9,
                color: "blue",
                textDecoration: "underline",
                marginBottom: 1,
              }}
            >
              {poDetails ? poDetails.company_website : `https://kgbuildtech.com`}
            </Text>
            <Text style={{ fontSize: 9, marginBottom: 1 }}>
              {poDetails ? `GST NO: ${poDetails.gst_no}` : ``}
            </Text>
          </View>

          {/* Right: PO Metadata details */}
          <View style={{ width: "50%", padding: 7 }}>
            {/* Row 1: PO Order No */}
            <View style={{ flexDirection: "row", marginBottom: 3 }}>
              <Text style={{ fontSize: 9, width: "42%", fontFamily: "Helvetica-Bold" }}>
                {poDetails.order_type == "1" ? "PO Order No" : "WO Order No"}
              </Text>
              <Text style={{ fontSize: 9, width: "5%", fontFamily: "Helvetica-Bold" }}>:</Text>
              <Text style={{ fontSize: 9, flex: 1 }}>{poDetails.po_no}</Text>
            </View>
            {/* Row 2: PO Classification */}
            <View style={{ flexDirection: "row", marginBottom: 3 }}>
              <Text style={{ fontSize: 9, width: "42%", fontFamily: "Helvetica-Bold" }}>
                {poDetails.order_type == "1" ? "PO Classification" : "WO Classification"}
              </Text>
              <Text style={{ fontSize: 9, width: "5%", fontFamily: "Helvetica-Bold" }}>:</Text>
              <Text style={{ fontSize: 9, flex: 1 }}>{poDetails.clasification_name}</Text>
            </View>
            {/* Row 3: Date */}
            <View style={{ flexDirection: "row", marginBottom: 3 }}>
              <Text style={{ fontSize: 9, width: "42%", fontFamily: "Helvetica-Bold" }}>
                Date
              </Text>
              <Text style={{ fontSize: 9, width: "5%", fontFamily: "Helvetica-Bold" }}>:</Text>
              <Text style={{ fontSize: 9, flex: 1 }}>
                {moment(poDetails.po_date).format("DD-MM-YYYY")}
              </Text>
            </View>
            {/* Row 4: Site Engineer */}
            <View style={{ flexDirection: "row", marginBottom: 3 }}>
              <Text style={{ fontSize: 9, width: "42%", fontFamily: "Helvetica-Bold" }}>
                Site Engineer
              </Text>
              <Text style={{ fontSize: 9, width: "5%", fontFamily: "Helvetica-Bold" }}>:</Text>
              <Text style={{ fontSize: 9, flex: 1, color: "#0056b3", fontFamily: "Helvetica-Bold" }}>
                {poDetails.site_eng_name}
              </Text>
            </View>
            {/* Row 5: Supplier Ref */}
            <View style={{ flexDirection: "row", marginBottom: 3 }}>
              <Text style={{ fontSize: 9, width: "42%", fontFamily: "Helvetica-Bold" }}>
                Supplier Ref
              </Text>
              <Text style={{ fontSize: 9, width: "5%", fontFamily: "Helvetica-Bold" }}>:</Text>
              <Text style={{ fontSize: 9, flex: 1 }}>
                {poDetails.quotation_no}
              </Text>
            </View>
            {/* Row 6: Contact */}
            <View style={{ flexDirection: "row", marginBottom: 3 }}>
              <Text style={{ fontSize: 9, width: "42%", fontFamily: "Helvetica-Bold" }}>
                Contact
              </Text>
              <Text style={{ fontSize: 9, width: "5%", fontFamily: "Helvetica-Bold" }}>:</Text>
              <Text style={{ fontSize: 9, flex: 1, color: "#0056b3" }}>
                {poDetails.site_eng_contact}
              </Text>
            </View>
          </View>
        </View>

        {/* ── BILL TO | PLACE OF SUPPLY ── */}
        <View
          style={{
            flexDirection: "row",
            borderLeft: "1 solid #ccc",
            borderRight: "1 solid #ccc",
            borderBottom: "1 solid #ccc",
            marginBottom: 0,
          }}
        >
          {/* Bill To */}
          <View style={{ width: "50%", padding: 7 }}>
            <Text style={{ fontSize: 9.5, fontFamily: "Helvetica-Bold", marginBottom: 2 }}>
              Bill To :
            </Text>
            <Text
              style={{
                fontSize: 10,
                color: "#0056b3",
                fontFamily: "Helvetica-Bold",
                marginBottom: 2,
              }}
            >
              {poDetails.vendor_name}
            </Text>
            <Text style={{ fontSize: 9, marginBottom: 1 }}>{poDetails.vendor_mobile}</Text>
            <Text style={{ fontSize: 9, marginBottom: 1 }}>{poDetails.vendor_street}</Text>
            <Text style={{ fontSize: 9, marginBottom: 1 }}>{poDetails.vendor_city}</Text>
            {poDetails.vendor_state?.toLowerCase() !== "tamilnadu" && (
              <Text style={{ fontSize: 9, marginBottom: 1 }}>
                {poDetails.vendor_state}, India
              </Text>
            )}
            <Text style={{ fontSize: 9, marginBottom: 1 }}>{poDetails.vendor_pincode}</Text>
            <Text style={{ fontSize: 9, fontFamily: "Helvetica-Bold" }}>
              GSTIN : {poDetails.vendor_gst}
            </Text>
          </View>

          {/* Place of Supply */}
          <View style={{ width: "50%", padding: 7 }}>
            <Text style={{ fontSize: 9.5, fontFamily: "Helvetica-Bold", marginBottom: 2 }}>
              Place of Supply :
            </Text>
            <Text
              style={{
                fontSize: 10,
                color: "#0056b3",
                fontFamily: "Helvetica-Bold",
                marginBottom: 2,
              }}
            >
              {projectDetails.name}
            </Text>
            <Text style={{ fontSize: 9, marginBottom: 1 }}>
              {projectDetails.project_address}
            </Text>
            <Text style={{ fontSize: 9, marginBottom: 1 }}>{projectDetails.city}</Text>
            {projectDetails.state?.toLowerCase() !== "tamilnadu" && (
              <Text style={{ fontSize: 9, marginBottom: 1 }}>
                {projectDetails.state}, India
              </Text>
            )}
            <Text style={{ fontSize: 9, marginBottom: 1 }}>{projectDetails.pincode}</Text>
          </View>
        </View>

        {/* ── ITEMS TABLE ── */}
        <View style={{ marginTop: 6 }}>
          {/* Table Header */}
          <View
            style={{
              flexDirection: "row",
              borderTop: "1 solid black",
              borderBottom: "1 solid black",
              backgroundColor: "#e8e8e8",
            }}
          >
            <Text
              style={{
                width: "7%",
                padding: 3,
                fontSize: 7.5,
                textAlign: "center",
                borderRight: "1 solid black",
                borderLeft: "1 solid black",
                fontFamily: "Helvetica-Bold",
              }}
            >
              S.No
            </Text>
            <Text
              style={{
                width: "40%",
                padding: 3,
                fontSize: 7.5,
                textAlign: "center",
                borderRight: "1 solid black",
                fontFamily: "Helvetica-Bold",
              }}
            >
              Description of Goods
            </Text>
            <Text
              style={{
                width: "8%",
                padding: 3,
                fontSize: 7.5,
                textAlign: "center",
                borderRight: "1 solid black",
                fontFamily: "Helvetica-Bold",
              }}
            >
              Qty
            </Text>
            <Text
              style={{
                width: "10%",
                padding: 3,
                fontSize: 7.5,
                textAlign: "center",
                borderRight: "1 solid black",
                fontFamily: "Helvetica-Bold",
              }}
            >
              Amt(Rs)
            </Text>
            <Text
              style={{
                width: "10%",
                padding: 3,
                fontSize: 7.5,
                textAlign: "center",
                borderRight: "1 solid black",
                fontFamily: "Helvetica-Bold",
              }}
            >
              Discount
            </Text>
            <Text
              style={{
                width: "10%",
                padding: 3,
                fontSize: 7.5,
                textAlign: "center",
                borderRight: "1 solid black",
                fontFamily: "Helvetica-Bold",
              }}
            >
              GST
            </Text>
            <Text
              style={{
                width: "15%",
                padding: 3,
                fontSize: 7.5,
                textAlign: "center",
                borderRight: "1 solid black",
                fontFamily: "Helvetica-Bold",
              }}
            >
              Total Amt
            </Text>
          </View>

          {/* Table Rows */}
          {paymentList &&
            paymentList.map((item, index) => (
              <View
                key={index}
                wrap={false}
                style={{
                  flexDirection: "row",
                  borderBottom: "1 solid black",
                  minHeight: 28,
                }}
              >
                {/* S.No */}
                <View
                  style={{
                    width: "7%",
                    borderRight: "1 solid black",
                    borderLeft: "1 solid black",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: 3,
                  }}
                >
                  <Text style={{ fontSize: 9, textAlign: "center" }}>{index + 1}</Text>
                </View>

                {/* Description */}
                <View
                  style={{
                    width: "40%",
                    borderRight: "1 solid black",
                    justifyContent: "center",
                    padding: 3,
                  }}
                >
                  <Text style={{ fontSize: 9, textAlign: "left" }}>{item.item_name}</Text>
                </View>

                {/* Qty */}
                <View
                  style={{
                    width: "8%",
                    borderRight: "1 solid black",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: 3,
                  }}
                >
                  <Text style={{ fontSize: 9, textAlign: "center" }}>
                    {twoDecimal(item.quantity)} {"\n"}({item.unit_name})
                  </Text>
                </View>

                {/* Amt */}
                <View
                  style={{
                    width: "10%",
                    borderRight: "1 solid black",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: 3,
                  }}
                >
                  <Text style={{ fontSize: 9, textAlign: "center" }}>
                    {twoDecimal(item.amount)}
                  </Text>
                </View>

                {/* Discount */}
                <View
                  style={{
                    width: "10%",
                    borderRight: "1 solid black",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: 3,
                  }}
                >
                  <Text style={{ fontSize: 9, textAlign: "center" }}>
                    {item.discount_type === "fixed"
                      ? `Rs. ${twoDecimal(item.discount_value)}`
                      : item.discount_type === "percentage"
                        ? `${item.discount_value}%`
                        : "—"}
                  </Text>
                </View>

                {/* GST */}
                <View
                  style={{
                    width: "10%",
                    borderRight: "1 solid black",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: 3,
                  }}
                >
                  <Text style={{ fontSize: 9, textAlign: "center" }}>{item.gst}%</Text>
                </View>

                {/* Total Amt */}
                <View
                  style={{
                    width: "15%",
                    borderRight: "1 solid black",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: 3,
                  }}
                >
                  <Text style={{ fontSize: 9, textAlign: "center" }}>
                    {twoDecimal(
                      item.discount_type === "fixed"
                        ? item.amount * item.quantity - item.discount_value
                        : item.discount_type === "percentage"
                          ? item.amount * item.quantity * (1 - item.discount_value / 100)
                          : item.amount * item.quantity
                    )}
                  </Text>
                </View>
              </View>
            ))}


          {/* Total without GST */}
          <View wrap={false} style={{ flexDirection: "row" }}>
            <Text
              style={{
                width: "7%",
                borderRight: "1 solid black",
                borderLeft: "1 solid black",
              }}
            ></Text>
            <Text style={{ width: "40%", borderRight: "1 solid black" }}></Text>
            <Text style={{ width: "8%", borderRight: "1 solid black" }}></Text>
            <Text
              style={{
                width: "30%",
                padding: 3,
                fontSize: 7.5,
                textAlign: "right",
                borderRight: "1 solid black",
                borderBottom: "1 solid black",
                fontFamily: "Helvetica-Bold",
              }}
            >
              Total amount without GST
            </Text>
            <Text
              style={{
                width: "15%",
                padding: 3,
                fontSize: 7.5,
                textAlign: "right",
                borderRight: "1 solid black",
                borderBottom: "1 solid black",
                fontFamily: "Helvetica-Bold",
              }}
            >
              {twoDecimal(poDetails.total_without_gst)}
            </Text>
          </View>

          {/* IGST or CGST/SGST */}
          {poDetails.total_igst > 0 ? (
            <View wrap={false} style={{ flexDirection: "row" }}>
              <Text
                style={{
                  width: "7%",
                  borderRight: "1 solid black",
                  borderLeft: "1 solid black",
                }}
              ></Text>
              <Text style={{ width: "40%", borderRight: "1 solid black" }}></Text>
              <Text style={{ width: "8%", borderRight: "1 solid black" }}></Text>
              <Text
                style={{
                  width: "30%",
                  padding: 3,
                  fontSize: 7.5,
                  textAlign: "right",
                  borderRight: "1 solid black",
                  borderBottom: "1 solid black",
                }}
              >
                IGST
              </Text>
              <Text
                style={{
                  width: "15%",
                  padding: 3,
                  fontSize: 7.5,
                  textAlign: "right",
                  borderRight: "1 solid black",
                  borderBottom: "1 solid black",
                }}
              >
                {twoDecimal(poDetails.total_igst)}
              </Text>
            </View>
          ) : (
            <>
              <View wrap={false} style={{ flexDirection: "row" }}>
                <Text
                  style={{
                    width: "7%",
                    borderRight: "1 solid black",
                    borderLeft: "1 solid black",
                  }}
                ></Text>
                <Text style={{ width: "40%", borderRight: "1 solid black" }}></Text>
                <Text style={{ width: "8%", borderRight: "1 solid black" }}></Text>
                <Text
                  style={{
                    width: "30%",
                    padding: 3,
                    fontSize: 7.5,
                    textAlign: "right",
                    borderRight: "1 solid black",
                    borderBottom: "1 solid black",
                  }}
                >
                  CGST
                </Text>
                <Text
                  style={{
                    width: "15%",
                    padding: 3,
                    fontSize: 7.5,
                    textAlign: "right",
                    borderRight: "1 solid black",
                    borderBottom: "1 solid black",
                  }}
                >
                  {twoDecimal(poDetails.total_cgst)}
                </Text>
              </View>
              <View wrap={false} style={{ flexDirection: "row" }}>
                <Text
                  style={{
                    width: "7%",
                    borderRight: "1 solid black",
                    borderLeft: "1 solid black",
                  }}
                ></Text>
                <Text style={{ width: "40%", borderRight: "1 solid black" }}></Text>
                <Text style={{ width: "8%", borderRight: "1 solid black" }}></Text>
                <Text
                  style={{
                    width: "30%",
                    padding: 3,
                    fontSize: 7.5,
                    textAlign: "right",
                    borderRight: "1 solid black",
                    borderBottom: "1 solid black",
                  }}
                >
                  SGST
                </Text>
                <Text
                  style={{
                    width: "15%",
                    padding: 3,
                    fontSize: 7.5,
                    textAlign: "right",
                    borderRight: "1 solid black",
                    borderBottom: "1 solid black",
                  }}
                >
                  {twoDecimal(poDetails.total_sgst)}
                </Text>
              </View>
            </>
          )}

          {/* Transport Charges */}
          <View wrap={false} style={{ flexDirection: "row" }}>
            <Text
              style={{
                width: "7%",
                borderRight: "1 solid black",
                borderLeft: "1 solid black",
              }}
            ></Text>
            <Text style={{ width: "40%", borderRight: "1 solid black" }}></Text>
            <Text style={{ width: "8%", borderRight: "1 solid black" }}></Text>
            <Text
              style={{
                width: "30%",
                padding: 3,
                fontSize: 7.5,
                textAlign: "right",
                borderRight: "1 solid black",
                borderBottom: "1 solid black",
              }}
            >
              Transport Charges (GST {poDetails.transport_gst}%)
            </Text>
            <Text
              style={{
                width: "15%",
                padding: 3,
                fontSize: 7.5,
                textAlign: "right",
                borderRight: "1 solid black",
                borderBottom: "1 solid black",
              }}
            >
              {twoDecimal(poDetails.transport_charge)}
            </Text>
          </View>

          {/* Payable Amount */}
          <View wrap={false} style={{ flexDirection: "row" }}>
            <Text
              style={{
                width: "7%",
                borderRight: "1 solid black",
                borderLeft: "1 solid black",
                borderBottom: "1 solid black",
              }}
            ></Text>
            <Text
              style={{
                width: "40%",
                borderRight: "1 solid black",
                borderBottom: "1 solid black",
              }}
            ></Text>
            <Text
              style={{
                width: "8%",
                borderRight: "1 solid black",
                borderBottom: "1 solid black",
              }}
            ></Text>
            <Text
              style={{
                width: "30%",
                padding: 3,
                fontSize: 7.5,
                textAlign: "right",
                borderRight: "1 solid black",
                borderBottom: "1 solid black",
                color: "#0056b3",
                fontFamily: "Helvetica-Bold",
              }}
            >
              Payable Amount
            </Text>
            <Text
              style={{
                width: "15%",
                padding: 3,
                fontSize: 7.5,
                textAlign: "right",
                borderRight: "1 solid black",
                borderBottom: "1 solid black",
                fontFamily: "Helvetica-Bold",
              }}
            >
              {twoDecimal(poDetails.total_with_gst)}
            </Text>
          </View>
        </View>

        {/* ── TERMS & CONDITIONS ── */}
        <View style={{ marginTop: 6, fontSize: 9.5, fontFamily: "Helvetica" }}>
          <View wrap={false} style={{ border: "1 solid black" }}>
            <Text
              style={{
                backgroundColor: "#eceff1",
                textAlign: "center",
                fontFamily: "Helvetica-Bold",
                padding: 4,
                fontSize: 10,
              }}
            >
              {poDetails.order_type == "1"
                ? "PO Terms & Condition(s):"
                : "WO Terms & Condition(s):"}
            </Text>
            <Text
              style={{
                textAlign: "center",
                padding: 4,
                fontSize: 9.5,
                color: "#2f3e55",
                borderTop: "1 solid black",
              }}
            >
              {stripHtml(poDetails?.terms)}
            </Text>
          </View>

          {/* Bank Details */}
          <View
            wrap={false}
            style={{
              marginTop: 8,
              padding: 6,
              border: "1 solid #2f3e55",
              borderRadius: 4,
              backgroundColor: "#f8f9fa",
            }}
          >
            <Text
              style={{
                fontFamily: "Helvetica-Bold",
                fontSize: 10,
                marginBottom: 4,
                color: "#2f3e55",
              }}
            >
              Bank Details
            </Text>
            <View style={{ flexDirection: "row", justifyContent: "space-between" }}>
              <View style={{ flex: 1, marginRight: 5 }}>
                <Text style={{ marginBottom: 2 }}>
                  <Text style={{ fontFamily: "Helvetica-Bold" }}>Bank Name: </Text>
                  {poDetails.bank_name}
                </Text>
                <Text style={{ marginBottom: 2 }}>
                  <Text style={{ fontFamily: "Helvetica-Bold" }}>Account Name: </Text>
                  {poDetails.account_name}
                </Text>
                <Text style={{ marginBottom: 2 }}>
                  <Text style={{ fontFamily: "Helvetica-Bold" }}>Account Number: </Text>
                  {poDetails.account_no}
                </Text>
              </View>
              <View style={{ flex: 1, marginLeft: 5 }}>
                <Text style={{ marginBottom: 2 }}>
                  <Text style={{ fontFamily: "Helvetica-Bold" }}>Branch: </Text>
                  {poDetails.branch}
                </Text>
                <Text style={{ marginBottom: 2 }}>
                  <Text style={{ fontFamily: "Helvetica-Bold" }}>IFSC Code: </Text>
                  {poDetails.ifsc_code}
                </Text>
              </View>
            </View>
          </View>
        </View>

        {/* ── SIGNATURES ── */}
        <View
          wrap={false}
          style={{
            marginTop: 25,
            flexDirection: "row",
            justifyContent: "space-between",
          }}
        >
          <Text style={{ fontFamily: "Helvetica-Bold", color: "#2f3e55", fontSize: 10 }}>
            Prepared By
          </Text>
          <Text style={{ fontFamily: "Helvetica-Bold", color: "#2f3e55", fontSize: 10 }}>
            Verified By
          </Text>
          <Text style={{ fontFamily: "Helvetica-Bold", color: "#2f3e55", fontSize: 10 }}>
            Approved By QS
          </Text>
          <Text style={{ fontFamily: "Helvetica-Bold", color: "#2f3e55", fontSize: 10 }}>
            Approved By
          </Text>
          <Text style={{ fontFamily: "Helvetica-Bold", color: "#2f3e55", fontSize: 10 }}>
            Contractor/Vendor Signature
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default PDFDocument;
